import { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store";
import { useLazyGetDatasetQuestionsQuery } from "@/store/api/dataset-api";
import { useSearchQuestionResponsesMutation } from "@/store/api/filters-api";
import {
  lineChartConfigSelector,
  lineChartConfigDispatcher,
  getCategoryRollupScope,
  SignificanceLevel,
  StatTestType,
  StatMarkerStyle,
  StatTestingTimeStrategy,
  type BreakoutQuestionOption,
  type LineChartConfig,
} from "@/store/slices/line-chart-config-slice";
import {
  transformLineChartData,
  type LineChartApiResponse,
} from "@/utils/chart-utils";
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import type { WidgetRollup } from "@models/Rollup";
import type { VisualizationBase } from "@models/VisualizationBase";
import { resolveVisualizationBaseFilters } from "@utils/visualization-base-search";
import { mergeBaseFiltersWithExistingFilters } from "@utils/visualization-base-utils";
import { filterFetchedDataToMetricResults } from "@utils/dataset-metric-representation-utils";


/**
 * Helper function to build crossColumnWeights array from perLineWeights for API request
 * Maps per-line weights to the format expected by backend (same as crosstab per-segment weights)
 */
const buildLineWeightsPayload = (
  config: LineChartConfig,
):
  | {
      surveyId: string;
      crossColumnLabel: string; // variableId from /search response
      weightVariableLabel: string;
    }[]
  | undefined => {
  if (config.weight !== "PER_LINE_WEIGHT") {
    return undefined;
  }

  if (
    !config.perLineWeights ||
    Object.keys(config.perLineWeights).length === 0
  ) {
    return undefined;
  }

  // lineId = series.id = variableId from /search response — use directly as crossColumnLabel
  const weights = Object.entries(config.perLineWeights).map(
    ([lineId, weight]) => ({
      surveyId: weight.surveyId,
      crossColumnLabel: lineId,
      weightVariableLabel: weight.weightVariableLabel,
    }),
  );

  return weights.length > 0 ? weights : undefined;
};

export const useLineChartConfigDrawer = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetId, questionId, activeFilters } =
    useAppSelector(lineChartConfigSelector);

  const [searchQuestionResponses, { isLoading: isRefetchingData }] =
    useSearchQuestionResponsesMutation();
  const [fetchDatasetQuestions] = useLazyGetDatasetQuestionsQuery();

  const { updateChartData } = lineChartConfigDispatcher(dispatch);

  const [localApplyFilters, setLocalApplyFilters] = useState(
    activeFilters.length > 0 ? true : false,
  );

  const isSaving = useRef(false);
  const isInitialLoad = useRef(false);
  const breakoutRef = useRef(currentConfig.breakout);
  breakoutRef.current = currentConfig.breakout;

  // Reusable function to fetch line chart data
  const fetchLineChartData = useCallback(
    async (
      applyFilters = false,
      overrideStatTesting?: {
        enabled: boolean;
        significanceLevel?: SignificanceLevel;
        minimumSampleSize?: number;
        testType?: StatTestType;
        timeStrategy?: StatTestingTimeStrategy;
      },
      overrideWeight?: "DISABLED" | "DATASET_DEFAULT" | "PER_LINE_WEIGHT",
      overrideTimeUnit?: "quarter" | "semester" | "month" | "week",
      overrideDates?: {
        startDate?: string | null;
        endDate?: string | null;
      },
      overridePeriodCount?: number | null,
      overrideBreakout?: {
        enabled: boolean;
        sourceLineId: string | null;
        questionId: string | null;
        focusMode?: string | null;
        crossFocusEntity?: string;
      } | null,
      overridePerLineWeights?: Record<
        string,
        {
          surveyId: string;
          configurationId: string;
          datasetElementQuestionId: string;
          datasetElementVariableId: string;
          weightVariableLabel: string;
        }
      >,
      overrideFocusParams?: { focusMode: string; focusEntity: string } | null,
      overrideRollup?: WidgetRollup | null,
      overrideCategoryRollups?: WidgetRollup[] | null,
      overrideVisualizationBase?: VisualizationBase | null,
      overrideUseTotalBase?: boolean,
      // Metric-sourced: the live metric's current source question, passed by
      // use-line-metric-source-revalidation.ts's handleRefresh so the very
      // first fetch after a definition change doesn't read the root
      // `questionId` before its own dispatch (updateResolvedQuestionId) has
      // propagated — same same-tick-stale-closure reasoning as the other
      // overrides above. Every other call site keeps reading the (by then
      // already-corrected) root `questionId`.
      overrideQuestionId?: string,
    ) => {
      if (!datasetId || !questionId) {
        console.warn(
          "Missing datasetId or questionId for line chart data fetch",
        );
        return;
      }

      const effectiveQuestionId = overrideQuestionId ?? questionId;

      try {
        // Determine which weight config to use
        const weightsToUse =
          overridePerLineWeights ?? currentConfig.perLineWeights;
        const hasPerLineWeights =
          weightsToUse && Object.keys(weightsToUse).length > 0;

        // Convert PER_LINE_WEIGHT to PER_SEGMENT_WEIGHT for API compatibility
        // BUT if no weights are configured, use DATASET_DEFAULT instead
        const weightSelection = overrideWeight ?? currentConfig.weight;
        let apiWeightSelection: string;
        if (weightSelection === "PER_LINE_WEIGHT") {
          apiWeightSelection = hasPerLineWeights
            ? "PER_SEGMENT_WEIGHT"
            : "DATASET_DEFAULT";
        } else {
          apiWeightSelection = weightSelection;
        }

        const requestBody: any = {
          questionId: effectiveQuestionId,
          datasetId: datasetId,
          topN: null,
          sorting: "ORIGINAL",
          weightSelection: apiWeightSelection,
          chartType: "LINE",
          timeUnit: (overrideTimeUnit ?? currentConfig.timeUnit).toUpperCase(),
        };

        if (
          weightSelection === "PER_LINE_WEIGHT" &&
          weightsToUse &&
          Object.keys(weightsToUse).length > 0
        ) {
          const lineWeights = Object.entries(weightsToUse).map(
            ([lineId, weight]) => ({
              surveyId: weight.surveyId,
              crossColumnLabel: lineId, // lineId = variableId from /search response
              weightVariableLabel: weight.weightVariableLabel,
            }),
          );
          console.log("✅ Adding crossColumnWeights to request:", lineWeights);
          requestBody.crossColumnWeights = lineWeights;
        } else {
          console.log("❌ NOT adding crossColumnWeights - condition failed");
        }

        // Use override dates if provided, otherwise use current config.
        // Use explicit undefined check so that null overrides (meaning "no end date") are respected.
        const startDate = overrideDates !== undefined ? overrideDates.startDate : currentConfig.startDate;
        const endDate = overrideDates !== undefined ? overrideDates.endDate : currentConfig.endDate;

        // Guard: if resolved endDate is before startDate (e.g. availability date moved
        // earlier than the configured start), skip the API call — the empty state
        // message handles this case in the UI.
        if (startDate && endDate && endDate < startDate) {
          updateChartData({ series: [], categories: [] });
          return;
        }

        // Add date range if both dates are specified
        if (startDate && endDate) {
          requestBody.startDate = startDate;
          requestBody.endDate = endDate;
        }

        // Add focus parameters if they exist (rollup takes precedence over single-entity focus).
        // Overrides let callers pass fresh values immediately, avoiding a stale-closure
        // refetch while waiting for the Redux state update to propagate.
        const effectiveRollup =
          overrideRollup !== undefined ? overrideRollup : currentConfig.focusRollup;
        const effectiveFocusMode =
          overrideFocusParams !== undefined
            ? overrideFocusParams?.focusMode
            : currentConfig.focusMode;
        const effectiveFocusEntity =
          overrideFocusParams !== undefined
            ? overrideFocusParams?.focusEntity
            : currentConfig.focusEntity;

        if (effectiveRollup) {
          requestBody.focusRollup = effectiveRollup;
          if (effectiveFocusMode) {
            requestBody.focusMode = effectiveFocusMode;
          }
        } else if (effectiveFocusMode && effectiveFocusEntity) {
          requestBody.focusMode = effectiveFocusMode;
          requestBody.focusEntity = effectiveFocusEntity;
        }

        // Use override if provided, otherwise use current config
        const statTestingEnabled = overrideStatTesting
          ? overrideStatTesting.enabled
          : currentConfig.statisticalTesting?.enabled;

        // Add statistical testing parameters if enabled AND dates are available (not all_time)
        if (statTestingEnabled && startDate && endDate) {
          // Use the new test type value if provided, otherwise fall back to Redux state
          const testType = overrideStatTesting?.testType
            ? overrideStatTesting.testType
            : currentConfig.statisticalTesting?.testType || "t-test";

          requestBody.enableStatTesting = true;
          requestBody.statTestingType = testTypeToApiFormat(testType);
          requestBody.pValue = significanceLevelToPValue(
            overrideStatTesting?.significanceLevel ||
              currentConfig.statisticalTesting?.significanceLevel ||
              "90%",
          );
          requestBody.minimumSampleSize =
            overrideStatTesting?.minimumSampleSize ||
            currentConfig.statisticalTesting?.minimumSampleSize ||
            30;
          requestBody.statTestingTimeStrategy =
            overrideStatTesting?.timeStrategy ??
            currentConfig.statisticalTesting?.timeStrategy ??
            "PREVIOUS_PERIOD";
        }

        // Explicitly set periodCount from override to avoid stale state
        // This matches Crosstab's pattern for rolling average handling
        if (overridePeriodCount !== undefined) {
          if (overridePeriodCount !== null) {
            requestBody.periodCount = overridePeriodCount;
          } else {
            // Explicitly mark as null to disable rolling average
            requestBody.periodCount = null;
          }
        } else if (currentConfig.rollingAverage?.enabled) {
          // Fallback to current config if no override provided
          requestBody.periodCount = currentConfig.rollingAverage.windowSize;
        }

        // Add breakout parameters — use override (fresh values) if provided, else Redux state
        const breakout =
          overrideBreakout !== undefined
            ? overrideBreakout
            : currentConfig.breakout;
        if (breakout?.enabled && breakout.sourceLineId && breakout.questionId) {
          requestBody.enableLineBreakout = true;
          requestBody.breakoutSourceLine = breakout.sourceLineId;
          requestBody.crossQuestionId = breakout.questionId;
          const focusMode =
            overrideBreakout?.focusMode ?? breakoutRef.current?.focusMode;
          const crossFocusEntity =
            overrideBreakout?.crossFocusEntity ??
            breakoutRef.current?.crossFocusEntity;
          if (focusMode && crossFocusEntity) {
            requestBody.crossFocusMode = focusMode;
            requestBody.crossFocusEntity = crossFocusEntity;
          }
        }

        // Category-dimension rollups (merge several lines into one), scoped by focus
        // mode — mutually exclusive with breakout, so never sent alongside it.
        const isBreakoutActive = !!(
          breakout?.enabled &&
          breakout.sourceLineId &&
          breakout.questionId
        );
        if (!isBreakoutActive) {
          const categoryRollupScope = getCategoryRollupScope(effectiveFocusMode);
          const effectiveCategoryRollups =
            overrideCategoryRollups !== undefined
              ? overrideCategoryRollups
              : currentConfig.categoryRollupsByScope?.[categoryRollupScope];
          if (effectiveCategoryRollups !== undefined) {
            requestBody.categoryRollups =
              effectiveCategoryRollups && effectiveCategoryRollups.length > 0
                ? effectiveCategoryRollups
                : null;
          }
        }

        const dashboardFilters =
          applyFilters && activeFilters && activeFilters.length > 0
            ? activeFilters
            : [];
        const effectiveVisualizationBase =
          overrideVisualizationBase !== undefined
            ? overrideVisualizationBase
            : currentConfig.visualizationBase;
        const baseFilters = await resolveVisualizationBaseFilters(
          dispatch,
          datasetId,
          effectiveVisualizationBase,
        );
        const combinedFilters = mergeBaseFiltersWithExistingFilters(
          dashboardFilters,
          baseFilters,
        );
        requestBody.filters =
          combinedFilters.length > 0 ? combinedFilters : undefined;

        // Metric-sourced: mirror the metric's own Metric population choice,
        // same as Bar Chart (useBarChartData.ts) and Scorecard's Dataset
        // Metric rows. overrideUseTotalBase takes precedence — a manual
        // Refresh dispatches the live population to Redux and calls this in
        // the same synchronous tick, before that dispatch's update is
        // visible in this closure's currentConfig.
        if (currentConfig.chartSourceType === "metric") {
          requestBody.useTotalBase =
            overrideUseTotalBase ??
            currentConfig.datasetMetricPopulation?.mode === "total_survey_population";
        }

        console.log("📤 Final API Request Body:", requestBody);

        const result = await searchQuestionResponses(requestBody).unwrap();

        // Metric-sourced: narrow the raw response down to exactly the
        // metric's own represented results — a no-op for every source kind
        // except multiple_one_dimensional/numeric (no request-level focus
        // for a variable subset), plus a defensive drift guard for the rest.
        // Skipped while Breakout is active: the response then contains one
        // row per (represented result x breakout segment), whose labels
        // don't match the metric's own matchLabels, so this filter would
        // wipe out the broken-out data instead of narrowing it.
        const narrowedResult =
          currentConfig.chartSourceType === "metric" && !isBreakoutActive
            ? {
                ...result,
                data: filterFetchedDataToMetricResults(
                  (result as LineChartApiResponse).data,
                  currentConfig.datasetMetricRepresentedResults ?? [],
                ),
              }
            : result;

        const transformedData = transformLineChartData(
          narrowedResult as LineChartApiResponse,
        );

        updateChartData({
          series: transformedData.series,
          categories: transformedData.categories,
          responsesMinDate: transformedData.responsesMinDate,
          responsesMaxDate: transformedData.responsesMaxDate,
          effectiveRollingPeriodCount:
            transformedData.effectiveRollingPeriodCount,
          rollingPeriodsWarningMessage:
            transformedData.rollingPeriodsWarningMessage,
          comparisonRollingLabel: transformedData.comparisonRollingLabel,
          missingRollingPeriodLabels: transformedData.missingRollingPeriodLabels,
        });
      } catch (error) {
        console.error("Error fetching line chart data:", error);
      }
    },
    [
      datasetId,
      questionId,
      activeFilters,
      currentConfig.timeUnit,
      currentConfig.weight,
      currentConfig.perLineWeights,
      currentConfig.startDate,
      currentConfig.endDate,
      currentConfig.focusMode,
      currentConfig.focusEntity,
      currentConfig.focusRollup,
      currentConfig.categoryRollupsByScope,
      currentConfig.statisticalTesting?.enabled,
      currentConfig.statisticalTesting?.significanceLevel,
      currentConfig.statisticalTesting?.minimumSampleSize,
      currentConfig.breakout?.enabled,
      currentConfig.breakout?.sourceLineId,
      currentConfig.breakout?.questionId,
      currentConfig.breakout?.focusMode,
      currentConfig.breakout?.crossFocusEntity,
      currentConfig.visualizationBase,
      currentConfig.chartSourceType,
      currentConfig.datasetMetricPopulation,
      currentConfig.datasetMetricRepresentedResults,
      dispatch,
      searchQuestionResponses,
      updateChartData,
    ],
  );

  // Fetch eligible breakout questions (Single + Multiple only, no Float/Numeric)
  const fetchBreakoutQuestions = useCallback(
    async (
      searchQuery?: string,
      page = 1,
    ): Promise<{ questions: BreakoutQuestionOption[]; hasMore: boolean }> => {
      if (!datasetId) return { questions: [], hasMore: false };

      const result = await fetchDatasetQuestions({
        id: datasetId,
        question_type: ["SINGLE", "MULTI"],
        search_query: searchQuery,
        page,
        page_size: 20,
      });

      if (!result.data) return { questions: [], hasMore: false };

      const questions: BreakoutQuestionOption[] = result.data.questions.map(
        (q) => ({
          id: q.datasetElementId,
          label: q.label,
          title: q.title,
          type: q.type === "multi" ? "Multiple" : "Single",
          variableCount: q.variablesCount,
          valueCount: q.valuesCount,
        }),
      );

      return {
        questions,
        hasMore: result.data.pagination.hasNextPage,
      };
    },
    [datasetId, fetchDatasetQuestions],
  );

  // Handler for filter toggle - directly fetch data, no useEffect
  const handleFilterToggle = async (checked: boolean) => {
    setLocalApplyFilters(checked);
    if (datasetId && questionId) {
      await fetchLineChartData(checked);
    }
  };

  // Handle statistical testing changes with refetch
  const handleStatTestingChange = useCallback(
    async (
      updateFn: () => void,
      newValues?: {
        enabled?: boolean;
        significanceLevel?: SignificanceLevel;
        minimumSampleSize?: number;
        testType?: StatTestType;
        markerStyle?: StatMarkerStyle;
        timeStrategy?: StatTestingTimeStrategy;
      },
    ) => {
      // Update Redux state
      updateFn();

      // Skip refetch if we're in initial load (drawer just opened)
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        return;
      }

      // Refetch data immediately with the new values passed directly
      // This avoids waiting for Redux state to update
      if (datasetId && questionId && newValues !== undefined) {
        // Build the override object with current values + new values
        const overrideStatTesting = {
          enabled:
            newValues.enabled ??
            currentConfig.statisticalTesting?.enabled ??
            false,
          significanceLevel:
            newValues.significanceLevel ??
            currentConfig.statisticalTesting?.significanceLevel ??
            "90%",
          minimumSampleSize:
            newValues.minimumSampleSize ??
            currentConfig.statisticalTesting?.minimumSampleSize ??
            30,
          testType:
            newValues.testType ??
            currentConfig.statisticalTesting?.testType ??
            "t-test",
          timeStrategy:
            newValues.timeStrategy ??
            currentConfig.statisticalTesting?.timeStrategy ??
            "PREVIOUS_PERIOD",
        };

        await fetchLineChartData(localApplyFilters, overrideStatTesting);
      }
    },
    [
      datasetId,
      questionId,
      fetchLineChartData,
      localApplyFilters,
      currentConfig.statisticalTesting,
    ],
  );

  // Handle rolling average changes with refetch
  const handleRollingAverageChange = useCallback(
    async (overridePeriodCount?: number | null) => {
      // Skip refetch if we're in initial load (drawer just opened)
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        return;
      }

      // Refetch data immediately with the new rolling average setting
      if (datasetId && questionId) {
        await fetchLineChartData(
          localApplyFilters,
          undefined,
          undefined,
          undefined,
          undefined,
          overridePeriodCount,
        );
      }
    },
    [datasetId, questionId, fetchLineChartData, localApplyFilters],
  );

  return {
    isRefetchingData,
    fetchLineChartData,
    localApplyFilters,
    handleFilterToggle,
    handleStatTestingChange,
    handleRollingAverageChange,
    fetchBreakoutQuestions,
    isSaving,
  };
};
